import { FC, useEffect, useRef, type CSSProperties } from 'react';
import { V4 as C } from '@components/v4/tokens';
import { Z } from '@components/v4';
import type { Nudge } from './usePaulinaChat';

/**
 * PaulinaNudgeTeaserMobile — proactividad de Paulina en MÓVIL: mini-bubble teaser
 * anclado al avatar del header (MobileTopBarV4), que muestra SOLO el título del nudge
 * (`nudge.titulo`) en una línea. Es un TEASER, no el nudge completo: el mensaje y el CTA
 * viven dentro del sheet (PaulinaConversation), que se abre al tocar el teaser/avatar.
 *
 * Antes en móvil un nudge proactivo solo encendía un dot verde pasivo en el avatar (el
 * contenido nunca se veía); este teaser lo vuelve proactivo sin ser intrusivo: aparece
 * solo, se auto-oculta a ~8 s dejando el dot como indicador persistente, y es descartable
 * antes con la X o con tap-away (lo gestiona el contenedor, MobileTopBarV4).
 *
 * Molde visual del PaulinaNudgeBubble desktop pero COMPACTO (1 línea, caret hacia ARRIBA
 * apuntando al avatar). LECCIÓN heredada (PriceCoachmark/chat14): base `opacity:1` y animar
 * SOLO `translateY` — un keyframe con opacidad dentro de un contenedor con transform puede
 * quedar stuck en 0. La animación de entrada respeta `prefers-reduced-motion` (solo corre
 * bajo `@media (prefers-reduced-motion: no-preference)`).
 *
 * z-index `Z.paulinaSheet` (95): el teaser cuelga del header (z-100) pero por encima del
 * resto del contenido; sigue por debajo del toast de pedido (Z.pedido=99) — aunque el gate
 * `showPaulina` del header ya lo suprime con pedido entrante.
 */

const KF_ID = 'plick-paulina-teaser-kf';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-teaser-in{from{transform:translateY(-6px)}to{transform:translateY(0)}}' +
    '.plick-paulina-teaser-anim{animation:plick-paulina-teaser-in 220ms ease-out both}}';
  document.head.appendChild(st);
}

interface PaulinaNudgeTeaserMobileProps {
  nudge: Nudge;
  /** Abre el sheet (toggleOpen) y deja ver el nudge completo dentro. */
  onOpen: () => void;
  /** Descarta el teaser sin abrir el sheet (deja el dot verde como indicador). */
  onDismiss: () => void;
}

export const PaulinaNudgeTeaserMobile: FC<PaulinaNudgeTeaserMobileProps> = ({ nudge, onOpen, onDismiss }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Ref siempre-actual de onDismiss: el padre (MobileTopBarV4) lo pasa inline (() => setTeaserVisible(false))
  // y re-renderiza con cada notificación (useNotificationContext), así que su referencia es inestable.
  // Sin esto, el useEffect de abajo re-suscribiría el listener global de pointerdown en cada render y
  // podría perder taps-away en la ventana entre remove y add. Mismo patrón que usePaulinaDialogA11y.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Tap-away: un toque fuera del teaser (incluido el avatar, que abre el sheet por su cuenta)
  // lo descarta dejando el dot. Escucha en captura para anticiparse a otros handlers. El tap
  // DENTRO del teaser no descarta (sus propios onClick gobiernan: abrir / X). pointerdown cubre
  // touch y mouse en un solo evento. Deps vacías: el listener se monta una sola vez (lee la ref).
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismissRef.current();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, []);

  // Anclado bajo el avatar (el header mide 52px). Ocupa el ancho disponible a la derecha
  // sin desbordar: pegado al borde derecho con un margen, ancho máximo acotado.
  const bubble: CSSProperties = {
    position: 'absolute',
    top: 52,
    right: 8,
    zIndex: Z.paulinaSheet,
    maxWidth: 'min(74vw, 280px)',
    background: C.primary,
    color: C.white,
    borderRadius: 12,
    padding: '9px 11px',
    boxShadow: '0 14px 36px rgba(37,55,70,.32)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    opacity: 1,
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={nudge.titulo}
      className="plick-paulina-teaser-anim"
      style={bubble}
      // El tap en el cuerpo abre el sheet (el nudge completo). La X (stopPropagation) descarta.
      onClick={onOpen}
    >
      {/* Caret hacia ARRIBA, apuntando al avatar del header. */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -5,
          // Alineado bajo el avatar (avatar a la izquierda de la campana, ~right 44px del header).
          right: 44,
          width: 12,
          height: 12,
          background: C.primary,
          transform: 'rotate(45deg)',
          borderRadius: 2,
        }}
      />
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Abrir: ${nudge.titulo}`}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 0,
          color: C.white,
          cursor: 'pointer',
          padding: 0,
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.25,
          // Teaser de 1 línea: trunca con elipsis, nunca multilínea ni desbordando el header.
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {nudge.titulo}
      </button>
      <button
        type="button"
        aria-label="Descartar"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: 0,
          color: 'rgba(255,255,255,.7)',
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};
